import { useEffect, useRef, useState } from 'react';
import { PlayCircle } from 'lucide-react';
import { resolveMediaUrl, getVideoEmbedUrl } from '../../lib/mediaApi';
import { registerPlayer, notifyPlaying } from '../../lib/videoPlaybackRegistry';
import { loadYouTubeIframeApi, type YTPlayerInstance } from '../../lib/youtubeIframeApi';
import { cn } from '../../lib/cn';

interface PlayableVideoProps {
  src: string;
  title: string;
  className: string;
}

/** Adds the params YouTube's/Vimeo's postMessage APIs need to accept
 * commands and report state changes from this page, plus autoplay when this
 * is rendered right after the visitor clicked the facade's play button (a
 * real user gesture, so autoplay isn't blocked). */
function withPlayerApiParams(embedUrl: string, autoplay: boolean): string {
  try {
    const u = new URL(embedUrl);
    if (u.hostname === 'www.youtube.com' || u.hostname === 'www.youtube-nocookie.com') {
      u.searchParams.set('enablejsapi', '1');
      if (autoplay) u.searchParams.set('autoplay', '1');
      if (typeof window !== 'undefined') u.searchParams.set('origin', window.location.origin);
    } else if (u.hostname === 'player.vimeo.com') {
      u.searchParams.set('api', '1');
      if (autoplay) u.searchParams.set('autoplay', '1');
    }
    return u.toString();
  } catch {
    return embedUrl;
  }
}

/** Extracts a static thumbnail for a YouTube embed URL, so the heavy
 * YouTube player (its JS/CSS payload runs into the hundreds of KiB, plus
 * third-party cookies and its own layout-thrashing scripts) only loads once
 * a visitor actually presses play, instead of on every page load. No public
 * thumbnail endpoint exists for Vimeo without an extra API call, so Vimeo
 * (and direct-upload videos) keep rendering immediately as before. */
function youtubeThumbnail(embedUrl: string | null): string | null {
  if (!embedUrl) return null;
  try {
    const u = new URL(embedUrl);
    if (!u.hostname.endsWith('youtube.com') && !u.hostname.endsWith('youtube-nocookie.com')) return null;
    const id = u.pathname.match(/\/embed\/([^/]+)/)?.[1];
    return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
  } catch {
    return null;
  }
}

/** Shared <video>/YouTube/Vimeo embed used anywhere more than one video can
 * be on screen at once (e.g. the Reviews video carousel) — registers itself
 * with videoPlaybackRegistry so starting playback here pauses every other
 * embed on the page, and vice versa. YouTube gets full support via the
 * official IFrame Player API (bound to our already-rendered <iframe>, not
 * replacing it) — an earlier hand-rolled postMessage listener for YouTube's
 * raw "infoDelivery" events turned out unreliable, since the player only
 * starts sending those after being registered through that same API's own
 * handshake. Vimeo responds to being paused but can't report its own
 * "started playing" state without loading their player SDK, so a Vimeo
 * embed won't itself trigger pausing others — a reasonable trade-off since
 * this site's video content is YouTube/direct-upload today. */
export default function PlayableVideo({ src, title, className }: PlayableVideoProps) {
  const embedUrl = getVideoEmbedUrl(src);
  const thumbnail = youtubeThumbnail(embedUrl);
  // Vimeo/direct-upload have no facade (no thumbnail to show), so they keep
  // rendering immediately as before; only the YouTube case is gated.
  const [activated, setActivated] = useState(!thumbnail);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!activated || !embedUrl || !embedUrl.includes('youtube')) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    let ytPlayer: YTPlayerInstance | null = null;
    let cancelled = false;
    const player = { pause: () => ytPlayer?.pauseVideo() };
    const unregister = registerPlayer(player);

    loadYouTubeIframeApi().then((YT) => {
      if (cancelled) return;
      ytPlayer = new YT.Player(iframe, {
        events: {
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) notifyPlaying(player);
          },
        },
      });
    });

    return () => {
      cancelled = true;
      unregister();
      ytPlayer?.destroy();
    };
  }, [embedUrl, activated]);

  useEffect(() => {
    if (!activated || !embedUrl || !embedUrl.includes('vimeo.com')) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    const player = {
      pause: () => iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*'),
    };
    return registerPlayer(player);
  }, [embedUrl, activated]);

  useEffect(() => {
    if (embedUrl) return;
    const video = videoRef.current;
    if (!video) return;
    const player = { pause: () => video.pause() };
    const unregister = registerPlayer(player);
    const onPlay = () => notifyPlaying(player);
    video.addEventListener('play', onPlay);
    return () => {
      unregister();
      video.removeEventListener('play', onPlay);
    };
  }, [embedUrl]);

  if (embedUrl && !activated) {
    return (
      <button
        type="button"
        onClick={() => setActivated(true)}
        aria-label={`Play ${title}`}
        className={cn(className, 'relative block w-full cursor-pointer bg-ink border-0 p-0 overflow-hidden group')}
      >
        {thumbnail && (
          <img src={thumbnail} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        )}
        <span className="absolute inset-0 bg-ink/25 group-hover:bg-ink/35 transition-colors flex items-center justify-center">
          <PlayCircle size={56} className="text-paper drop-shadow-md" strokeWidth={1.5} />
        </span>
      </button>
    );
  }

  return embedUrl ? (
    <iframe
      ref={iframeRef}
      src={withPlayerApiParams(embedUrl, !!thumbnail)}
      title={title}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      className={className}
    />
  ) : (
    <video ref={videoRef} controls preload="metadata" aria-label={title} className={className}>
      <source src={resolveMediaUrl(src)} />
    </video>
  );
}
